import {
  Add,
  ExpandLess,
  ExpandMore,
  Fullscreen,
  FullscreenExit,
  Info,
  Remove,
  RestoreOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import mermaid from "mermaid";
import { useEffect, useRef, useState, useCallback, memo } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { useAtom } from "jotai";
import "../../../../Common/styles/mermaidOverrides.scss"; // Import global Mermaid overrides
import {
  getJiraWorkflowSchemes,
  JiraWorkflowScheme,
  Workflow,
} from "../../models/JiraWorkflowScheme";
import styles from "./JiraWorkflowSchemeSelector.module.scss";
import { JiraIssueTypeIconMap } from "../../models/JiraIssueTypeIcon";
import { selectedPackageAtom } from "../../hooks/useSelectPackage";

// Initialize mermaid with appropriate configuration
mermaid.initialize({
  startOnLoad: false,
  theme: "default",
  securityLevel: "loose",
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  flowchart: {
    htmlLabels: true,
    curve: "basis",
    useMaxWidth: true,
    wrappingWidth: 100, // Control text wrapping for labels
    padding: 5,
  },
  themeVariables: {
    edgeLabelBackground: "white", // White background for edge labels
    fontSize: "9px",
    labelTextColor: "#333", // Ensure label text is dark and readable
  },
});

// Define Jira Cloud status color constants
const JIRA_COLORS = {
  "TODO": "#42526E", // Blue-gray
  "IN_PROGRESS": "#0052CC", // Blue
  "DONE": "#00875A", // Green
};

interface JiraWorkflowSchemeSelectorProps<TForm extends FieldValues> {
  control: Control<TForm>;
  name: Path<TForm>;
  preselectedValue?: string | null;
}


function WorkflowCard({ workflow }: { workflow: Workflow }) {
  return (
    <Card variant="outlined" sx={{ mb: 1, backgroundColor: "#fbfefe" }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Typography variant="subtitle2" fontWeight="bold">{workflow.name}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{workflow.description}</Typography>
        <Box className={styles.workflowTypeStatusRow}>
          <Box className={styles.columnSection}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>Issue Types:</Typography>
            <Box display="flex" gap={1.5} flexWrap="wrap">
              {workflow.issueTypes.length === 0 ? (
                <Box key="all-issue-types" sx={{ display: "flex", alignItems: "center", border: "1px solid #ddd", borderRadius: "3px", padding: "2px 6px", gap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 500, fontSize: "0.7rem" }}>All</Typography>
                </Box>
              ) : workflow.issueTypes.map((issueType) => (
                <Box key={issueType.id} sx={{ display: "flex", alignItems: "center", border: "1px solid #ddd", borderRadius: "3px", padding: "2px 6px", gap: 1 }}>
                  <img src={JiraIssueTypeIconMap[issueType.name]} alt={issueType.name} style={{ width: "14px", height: "14px" }} />
                  <Typography variant="caption" sx={{ fontWeight: 500, fontSize: "0.7rem" }}>{issueType.name}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        <Box mt={2}>
          <WorkflowDiagram workflow={workflow} />
        </Box>
      </CardContent>
    </Card>
  );
}

const JiraWorkflowSchemeSelector = <TForm extends FieldValues>({
  control,
  name,
  preselectedValue = null
}: JiraWorkflowSchemeSelectorProps<TForm>) => {
  const [selectedPackage] = useAtom(selectedPackageAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedScheme, setExpandedScheme] = useState<string | null>(null);
  const [jiraWorkflowSchemes, setJiraWorkflowSchemes] = useState<JiraWorkflowScheme[]>([]);
  const [hasSetDefault, setHasSetDefault] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setHasSetDefault(false);
    getJiraWorkflowSchemes(selectedPackage?.name).then((schemes) => {
      setJiraWorkflowSchemes(schemes);
    }).finally(() => setIsLoading(false));
  }, [selectedPackage?.name]);

  const toggleSchemeExpand = (schemeId: string) => {
    setExpandedScheme(expandedScheme === schemeId ? null : schemeId);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, onChange, ...field } }) => {
        // Auto-select the first workflow scheme if no value is set
        // This needs to be done here to have access to onChange from field
        if (!hasSetDefault && !field.value && jiraWorkflowSchemes.length > 0) {
          setHasSetDefault(true);
          onChange(jiraWorkflowSchemes[0].id);
        }
        
        return (
          <Box className={styles.workflowSchemeSelector}>
            <Box display="flex" alignItems="center" mb={2}>
              <Typography variant="body2" fontWeight="bold" color="text.primary">
                Jira Workflow Scheme Selection
              </Typography>
              <Tooltip title="Select a workflow scheme that defines how issues will flow through different statuses in your Jira project">
                <IconButton size="small" sx={{ ml: 1 }}>
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose a workflow scheme that best fits your team's process. Each
              scheme contains workflows for different issue types with predefined
              statuses and transitions.
            </Typography>

            {isLoading && <CircularProgress size={24} />}
            {!isLoading && <RadioGroup
              value={field.value}
              onChange={(e) => onChange(e.target.value)}
              ref={ref}
            >
              {jiraWorkflowSchemes.map((scheme) => (
                <Card
                  key={scheme.id}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    border: field.value === scheme.id
                      ? "2px solid #00875A"
                      : "1px solid #e0e0e0",
                    backgroundColor: field.value === scheme.id ? "#ecf8f3" : "#fff",
                  }}
                >
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center" flex={1}>
                        <FormControlLabel
                          value={scheme.id}
                          control={<Radio size="small" sx={{ marginTop: "-8px" }} />}
                          label={<Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body1" fontWeight="bold">
                                {scheme.name}
                              </Typography>
                              {scheme.isDefault && (
                                <Chip
                                  label="Recommended"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ fontSize: "0.7rem", height: "20px" }} />
                              )}
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {scheme.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {scheme.workflows.length} workflow
                              {scheme.workflows.length !== 1 ? "s" : ""} included
                            </Typography>
                          </Box>}
                          sx={{
                            alignItems: "flex-start",
                            m: 0,
                            ".MuiFormControlLabel-label": {
                              marginTop: 0,
                              paddingTop: 0,
                            },
                          }} />
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() => toggleSchemeExpand(scheme.id)}
                        aria-label={expandedScheme === scheme.id ? "collapse" : "expand"}
                      >
                        {expandedScheme === scheme.id ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </IconButton>
                    </Box>

                    <Collapse in={expandedScheme === scheme.id}>
                      <Box mt={2} pl={4}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          Included Workflows:
                        </Typography>
                        {scheme.workflows.map((workflow) => (
                          <WorkflowCard key={workflow.id} workflow={workflow} />
                        ))}
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>
            }
          </Box>)
      }}
    />
  );
};

// Memoize the component to prevent unnecessary re-renders when parent re-renders
// Only re-render if name or preselectedValue actually changes
const MemoizedJiraWorkflowSchemeSelector = memo(
  JiraWorkflowSchemeSelector,
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if name or preselectedValue changes
    return prevProps.name === nextProps.name && 
           prevProps.preselectedValue === nextProps.preselectedValue;
  }
) as typeof JiraWorkflowSchemeSelector;

export default MemoizedJiraWorkflowSchemeSelector;

// Workflow Diagram Component

function WorkflowDiagram({ workflow }: { workflow: Workflow }) {
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mermaidContainerRef = useRef<HTMLDivElement>(null);
  const diagramContainerRef = useRef<HTMLDivElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);

  // Zoom/pan handlers
  const handleZoomIn = useCallback(() => setScale((s) => Math.min(s + 0.5, 3)), []);
  const handleZoomOut = useCallback(() => setScale((s) => Math.max(s - 0.5, 0.5)), []);
  const handleResetView = useCallback(() => { setScale(1); setPosition({ x: 0, y: 0 }); }, []);
  const toggleFullscreen = useCallback(() => { setIsFullscreen(f => !f); setPosition({ x: 0, y: 0 }); }, []);

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    e.preventDefault();
  };
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setPosition((prev) => ({ x: prev.x + (e.clientX - dragStart.x), y: prev.y + (e.clientY - dragStart.y) }));
    setDragStart({ x: e.clientX, y: e.clientY });
  };
  const handleMouseUp = () => setIsDragging(false);
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setScale((s) => Math.min(Math.max(0.5, s + e.deltaY * -0.005), 3));
  };

  // Mermaid diagram generation
  const generateMermaidDiagram = (workflow: Workflow): string => {
    let diagram = "graph TD\n";
    const statusColorMap = new Map<string, string>();
    workflow.statuses.forEach((status) => statusColorMap.set(status.id, JIRA_COLORS[status.statusCategory]));
    workflow.statuses.forEach((status) => { diagram += `  ${status.id}["${status.name}"]:::${status.id}\n`; });
    workflow.transitions.forEach((transition) => {
      diagram += `  ${transition.from ?? (transition.type === "GLOBAL" ? "All" : "New")} -- "${transition.name}" --> ${transition.to}\n`;
    });
    diagram += `  classDef New fill:#f4f5f7,color:#333,stroke:#333,stroke-width:1px;\n`;
    statusColorMap.forEach((color, statusId) => { diagram += `  classDef ${statusId} fill:${color},color:white,stroke:none;\n`; });
    diagram += `  linkStyle default stroke:#333333,stroke-width:1.0px;\n`;
    diagram += `%%{init: { 'themeVariables': { 'edgeLabelBackground': 'white', 'fontSize': '9px' } } }%%\n`;
    return diagram;
  };

  // Render Mermaid diagram
  useEffect(() => {
    setLoading(true);
    const diagramDefinition = generateMermaidDiagram(workflow);
    const renderDiagram = async () => {
      try {
        const { svg } = await mermaid.render(`diagram-${workflow.id}-${Date.now()}`, diagramDefinition);
        if (mermaidContainerRef.current) {
          mermaidContainerRef.current.innerHTML = svg;
          mermaidContainerRef.current.querySelectorAll("svg").forEach((svgEl: Element) => 
          {
            if (svgEl instanceof SVGElement) {
              svgEl.style.maxHeight = "250px";
            }
          });
          // Style edge labels
          mermaidContainerRef.current.querySelectorAll(".edgeLabel").forEach((label: Element) => {
            if (label instanceof HTMLElement) {
              label.style.backgroundColor = "transparent";
              label.querySelectorAll("div").forEach((div: Element) => {
                if (div instanceof HTMLElement) {
                  div.style.backgroundColor = "white";
                  div.style.padding = "2px 4px";
                  div.style.borderRadius = "3px";
                  div.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
                  div.style.display = "inline-block";
                  div.style.whiteSpace = "nowrap";
                }
              });
              label.querySelectorAll("rect").forEach((rect: Element) => {
                if (rect instanceof SVGRectElement) {
                  rect.setAttribute("fill", "white");
                  rect.setAttribute("rx", "3");
                  rect.setAttribute("ry", "3");
                }
              });
            }
          });
        }
        if (isFullscreen) {
          const fullscreenContainer = document.querySelector(`.fullscreen.workflow-diagram`);
          if (fullscreenContainer) {
            fullscreenContainer.innerHTML = svg;
            fullscreenContainer.querySelectorAll("svg").forEach((svgEl: Element) => 
            {
              if (svgEl instanceof SVGElement) {
                svgEl.style.maxHeight = "100%";
              }
            });
            fullscreenContainer.querySelectorAll(".edgeLabel").forEach((label: Element) => {
              if (label instanceof HTMLElement) {
                label.style.backgroundColor = "transparent";
                label.querySelectorAll("div").forEach((div: Element) => {
                  if (div instanceof HTMLElement) {
                    div.style.backgroundColor = "white";
                    div.style.padding = "2px 4px";
                    div.style.borderRadius = "3px";
                    div.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
                    div.style.display = "inline-block";
                    div.style.whiteSpace = "nowrap";
                  }
                });
                label.querySelectorAll("rect").forEach((rect: Element) => {
                  if (rect instanceof SVGRectElement) {
                    rect.setAttribute("fill", "white");
                    rect.setAttribute("rx", "3");
                    rect.setAttribute("ry", "3");
                  }
                });
              }
            });
          }
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    const timer = setTimeout(renderDiagram, 100);
    return () => clearTimeout(timer);
  }, [workflow, isFullscreen]);

  // Wheel event for zoom
  useEffect(() => {
    const regularContainer = diagramContainerRef.current;
    const fullscreenContainer = fullscreenContainerRef.current;
    const setupWheelHandlers = (container: HTMLElement | null) => {
      if (!container) return;
      const wheelHandler = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setScale((scale) =>
          Math.min(Math.max(0.5, scale + e.deltaY * -0.005), 3)
        );
      };
      container.addEventListener("wheel", wheelHandler, { passive: false });
      return () => container.removeEventListener("wheel", wheelHandler);
    };
    setupWheelHandlers(regularContainer);
    setupWheelHandlers(fullscreenContainer);
  }, [diagramContainerRef, fullscreenContainerRef, setScale, isFullscreen]);

  const transformStyle = {
    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
    transformOrigin: "center center",
    transition: isDragging ? "none" : "transform 0.1s ease",
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <>
      <Box sx={{ mb: 1, display: "flex", justifyContent: "flex-end" }}>
        <ButtonGroup size="small">
          <Tooltip title="Zoom In"><Button onClick={handleZoomIn}><Add fontSize="small" /></Button></Tooltip>
          <Tooltip title="Reset View"><Button onClick={handleResetView}><RestoreOutlined fontSize="small" /></Button></Tooltip>
          <Tooltip title="Zoom Out"><Button onClick={handleZoomOut}><Remove fontSize="small" /></Button></Tooltip>
          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}><Button onClick={toggleFullscreen}>{isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}</Button></Tooltip>
        </ButtonGroup>
      </Box>
      {!isFullscreen && (
        <Box
          ref={diagramContainerRef}
          className={styles.workflowDiagramContainer}
          sx={{ width: "100%", height: "250px", border: "1px solid #eee", borderRadius: "4px", overflow: "hidden", display: "flex", justifyContent: "center", alignItems: "center", position: "relative", backgroundColor: "#fff", mb: 1, touchAction: "none", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          {loading ? <CircularProgress size={40} /> : workflow.transitions.length === 0 ? <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>No transitions defined for this workflow.</Typography> : null}
          <div ref={mermaidContainerRef} className={`${styles.mermaidContainer} workflow-diagram`} style={{...transformStyle }} />
        </Box>
      )}
      <Dialog open={isFullscreen} onClose={toggleFullscreen} maxWidth="lg" fullWidth fullScreen PaperProps={{ sx: { backgroundColor: "#fff", position: "relative", overflow: "hidden" } }}>
        <DialogContent sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">{workflow.name} - Workflow Diagram</Typography>
            <ButtonGroup size="small">
              <Tooltip title="Zoom In"><Button onClick={handleZoomIn}><Add fontSize="small" /></Button></Tooltip>
              <Tooltip title="Reset View"><Button onClick={handleResetView}><RestoreOutlined fontSize="small" /></Button></Tooltip>
              <Tooltip title="Zoom Out"><Button onClick={handleZoomOut}><Remove fontSize="small" /></Button></Tooltip>
              <Tooltip title="Exit Fullscreen"><Button onClick={toggleFullscreen}><FullscreenExit fontSize="small" /></Button></Tooltip>
            </ButtonGroup>
          </Box>
          <Box ref={fullscreenContainerRef} sx={{ flexGrow: 1, border: "1px solid #eee", borderRadius: "4px", overflow: "hidden", display: "flex", justifyContent: "center", alignItems: "center", position: "relative", backgroundColor: "#fff", touchAction: "none", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onWheel={handleWheel}>
            {loading ? <CircularProgress size={60} /> : workflow.transitions.length === 0 ? <Typography variant="body1" color="text.secondary">No transitions defined for this workflow.</Typography> : null}
            <div className={`${styles.mermaidContainer} workflow-diagram fullscreen`} style={{ width: "100%", height: "100%", ...transformStyle }} />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
